import type { VisualSignalBus } from '../signals/VisualSignalBus.js';
import { PlaybackClock } from './PlaybackClock.js';
import type { ReplayData, ReplayInput, ReplayRhythmEvent } from './ReplaySchema.js';

/**
 * Plays a performance back into the signal bus.
 *
 * The whole design is in what it does *not* do. It does not touch a renderer, it
 * does not replay mutations, and it does not restore any visual state. It puts
 * the inputs and the events back on the bus at the times they happened, and
 * everything downstream — mapping, mutation, `ArtState`, the renderers — runs
 * exactly as it does live. That is what makes reinterpretation possible: swap
 * the recipe and the same performance produces a different artwork, because the
 * artwork was never in the file.
 *
 * Time comes from `PlaybackClock` and is pushed in, not read. Every event whose
 * moment has passed is emitted, however many frames that takes — a 200 ms stall
 * emits everything in those 200 ms on the next tick rather than losing it. This
 * is why the player is driven by `advance(delta)` and never by a frame counter.
 */

export type ReplayStatus = 'idle' | 'playing' | 'paused' | 'finished';

export interface ReplayPlayerOptions {
  readonly bus: VisualSignalBus;
  /** Bars per section, matching the live source, for reconstructed levels. */
  readonly beatsPerBar?: number;
}

export interface ReplayProgress {
  readonly status: ReplayStatus;
  readonly time: number;
  readonly duration: number;
  readonly inputsEmitted: number;
  readonly eventsEmitted: number;
  readonly totalInputs: number;
  readonly totalEvents: number;
  readonly playbackRate: number;
}

export class ReplayPlayer {
  readonly clock = new PlaybackClock();

  private readonly bus: VisualSignalBus;
  private replay: ReplayData | null = null;
  private inputs: readonly ReplayInput[] = [];
  private events: readonly ReplayRhythmEvent[] = [];
  private inputCursor = 0;
  private eventCursor = 0;
  private status: ReplayStatus = 'idle';
  /** Running totals rebuilt from the timeline, for the signal levels. */
  private score = 0;
  private combo = 0;
  private accuracy = 0;

  constructor(options: ReplayPlayerOptions) {
    this.bus = options.bus;
  }

  get loaded(): ReplayData | null {
    return this.replay;
  }

  get state(): ReplayStatus {
    return this.status;
  }

  load(replay: ReplayData): void {
    this.replay = replay;
    this.inputs = replay.timeline.playerInputs;
    this.events = replay.timeline.rhythmEvents;
    // A song's length, not the last event's: the trailing silence is part of the
    // performance and the visuals keep settling through it.
    this.clock.setDuration(Math.max(replay.song.durationSeconds, lastTime(replay)) + 0.5);
    this.restart();
  }

  play(): void {
    if (this.replay === null) return;
    this.clock.play();
    this.status = 'playing';
  }

  pause(): void {
    if (this.status !== 'playing') return;
    this.clock.pause();
    this.status = 'paused';
  }

  resume(): void {
    if (this.status !== 'paused') return;
    this.clock.play();
    this.status = 'playing';
  }

  stop(): void {
    this.clock.pause();
    this.status = 'idle';
  }

  /** Back to the beginning, ready to play. */
  restart(): void {
    this.clock.reset();
    this.inputCursor = 0;
    this.eventCursor = 0;
    this.score = 0;
    this.combo = 0;
    this.accuracy = 0;
    this.bus.reset();
    this.status = 'idle';
    if (this.replay !== null) {
      this.bus.setLevel('BPM', this.replay.song.bpm);
    }
  }

  /**
   * Jumps to a moment.
   *
   * Backwards seeks restart and fast-forward rather than trying to un-emit: the
   * bus has no notion of retracting a signal, and `ArtState` is an accumulator,
   * so the only honest way to reach an earlier moment is to rebuild from the
   * start. Forward seeks simply let the pump catch up, silently — everything
   * skipped is still applied so the state is right, which is what a scrub needs.
   */
  seek(seconds: number, options: { readonly silent?: boolean } = {}): void {
    if (this.replay === null) return;
    const target = Math.max(0, seconds);
    if (target < this.clock.currentTime) {
      const wasPlaying = this.status === 'playing';
      this.restart();
      if (wasPlaying) this.play();
    }
    this.clock.seek(target);
    this.pump(target, options.silent === true);
  }

  set playbackRate(rate: number) {
    this.clock.playbackRate = rate;
  }

  get playbackRate(): number {
    return this.clock.playbackRate;
  }

  /**
   * Advances by real elapsed seconds and emits everything that came due.
   *
   * Returns how many signals were emitted, which is what a caller wanting to
   * fast-forward headlessly loops on.
   */
  advance(deltaSeconds: number): number {
    if (this.replay === null || this.status !== 'playing') return 0;
    this.clock.advance(deltaSeconds);
    const emitted = this.pump(this.clock.currentTime, false);
    if (this.clock.finished && this.inputCursor >= this.inputs.length &&
        this.eventCursor >= this.events.length) {
      this.status = 'finished';
      this.clock.pause();
    }
    return emitted;
  }

  /**
   * Emits every timeline entry at or before `time`.
   *
   * The two cursors advance independently and are merged by time, so an input
   * and an event that share a moment come out in the order a live run would
   * have produced them: the press first, then what the game made of it.
   */
  private pump(time: number, silent: boolean): number {
    let emitted = 0;
    for (;;) {
      const nextInput = this.inputs[this.inputCursor];
      const nextEvent = this.events[this.eventCursor];
      const inputDue = nextInput !== undefined && nextInput.time <= time;
      const eventDue = nextEvent !== undefined && nextEvent.time <= time;
      if (!inputDue && !eventDue) break;

      const takeInput =
        inputDue && (!eventDue || (nextInput as ReplayInput).time <= (nextEvent as ReplayRhythmEvent).time);

      if (takeInput) {
        this.inputCursor += 1;
        if (!silent) this.emitInput(nextInput as ReplayInput);
      } else {
        this.eventCursor += 1;
        if (!silent) this.emitEvent(nextEvent as ReplayRhythmEvent);
        else this.applyLevels(nextEvent as ReplayRhythmEvent);
      }
      emitted += 1;
    }
    this.bus.setLevel('TIME', time);
    return emitted;
  }

  /** The same signal `VisualSignalSource.publishInput` produces during a run. */
  private emitInput(input: ReplayInput): void {
    this.bus.emit({
      name: 'PLAYER_INPUT',
      time: input.time,
      strength: input.judged ? 0.7 : 1,
      detail: {
        x: input.x,
        y: input.y,
        judged: input.judged,
        beatDistance: input.beatDistance,
        inputType: input.inputType,
        inputTime: input.time,
      },
    });
  }

  private emitEvent(event: ReplayRhythmEvent): void {
    this.applyLevels(event);
    switch (event.type) {
      case 'NOTE_HIT':
        this.bus.emit({
          name: 'NOTE_HIT',
          time: event.time,
          strength: gradeStrength(event.grade ?? 'perfect'),
          detail: {
            grade: event.grade ?? 'perfect',
            offsetMs: event.offsetMs ?? 0,
            combo: event.combo ?? 0,
            ...(event.eventIndex === undefined ? {} : { eventIndex: event.eventIndex }),
          },
        });
        break;
      case 'NOTE_MISS':
        this.bus.emit({
          name: 'NOTE_MISS',
          time: event.time,
          strength: 1,
          detail: {
            grade: 'miss',
            ...(event.eventIndex === undefined ? {} : { eventIndex: event.eventIndex }),
          },
        });
        break;
      case 'COMBO_BREAK':
        // Carried as a CUSTOM_EVENT exactly as the live runtime carries it, so
        // a recipe mapping it does not need to know whether this is a replay.
        this.bus.emit({
          name: 'CUSTOM_EVENT',
          time: event.time,
          strength: Math.min(1, (event.combo ?? 0) / 40),
          detail: { kind: 'comboBreak', brokenAt: event.combo ?? 0 },
        });
        break;
      case 'BAR':
        this.bus.emit({ name: 'BAR', time: event.time, strength: 1, detail: {} });
        break;
      case 'SECTION':
        this.bus.emit({ name: 'SECTION', time: event.time, strength: 1, detail: {} });
        break;
      case 'SONG_START':
      case 'SONG_END':
        this.bus.emit({
          name: 'SONG_EVENT',
          time: event.time,
          strength: 1,
          detail: { kind: event.type },
        });
        break;
      default:
        break;
    }
  }

  /** Restores the running levels a live run would have been publishing. */
  private applyLevels(event: ReplayRhythmEvent): void {
    if (event.score !== undefined) this.score = event.score;
    if (event.combo !== undefined) this.combo = event.combo;
    if (event.accuracy !== undefined) this.accuracy = event.accuracy;
    if (event.type === 'COMBO_BREAK' || event.type === 'NOTE_MISS') this.combo = 0;
    this.bus.setLevels({ SCORE: this.score, COMBO: this.combo, ACCURACY: this.accuracy });
  }

  progress(): ReplayProgress {
    return {
      status: this.status,
      time: this.clock.currentTime,
      duration: this.clock.durationSeconds,
      inputsEmitted: this.inputCursor,
      eventsEmitted: this.eventCursor,
      totalInputs: this.inputs.length,
      totalEvents: this.events.length,
      playbackRate: this.clock.playbackRate,
    };
  }
}

function lastTime(replay: ReplayData): number {
  const lastInput = replay.timeline.playerInputs.at(-1)?.time ?? 0;
  const lastEvent = replay.timeline.rhythmEvents.at(-1)?.time ?? 0;
  return Math.max(lastInput, lastEvent);
}

function gradeStrength(grade: string): number {
  switch (grade) {
    case 'perfect':
      return 1;
    case 'great':
      return 0.8;
    case 'good':
      return 0.6;
    case 'bad':
      return 0.4;
    default:
      return 0.5;
  }
}
